import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PauseCircle, Loader2 } from "lucide-react";
import { useApp, API } from "../context";

/**
 * In-app banner shown to a parent whose plan is paused. Reads the billing
 * snapshot from /api/payments/my-billing-state and shows "Your plan is paused
 * until [date]". Dismissible per session. Renders nothing when not paused.
 */
export default function PauseBanner() {
  const { user } = useApp();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/payments/my-billing-state?parent_id=${user.id}`)
      .then((r) => setState(r.data))
      .catch(() => {});
  }, [user?.id]);

  const resumeNow = async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/payments/resume-paused-subscription`, { parent_id: user.id });
      toast.success("Welcome back! Your plan is active again 🎉", {
        description: "Premium features are back on right away.",
        duration: 6000,
      });
      setState((s) => ({ ...s, paused: false }));
    } catch (e) {
      toast.error("Couldn't resume just now", { description: e.response?.data?.detail || "Please try again." });
    } finally {
      setResuming(false);
    }
  };

  if (!user || dismissed || !state?.paused) return null;

  let untilStr = "";
  if (state.pause_resumes_ts) {
    try {
      const ts = Number(state.pause_resumes_ts);
      const d = new Date(ts > 1e12 ? ts : ts * 1000);
      untilStr = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
    } catch { /* ignore */ }
  }

  return (
    <div className="w-full bg-gradient-to-r from-blue-600/20 via-cyan-500/20 to-blue-600/20 border-b border-blue-400/30" data-testid="pause-banner">
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center gap-2.5">
        <PauseCircle size={16} className="text-blue-300 shrink-0" />
        <p className="text-blue-100 text-xs sm:text-sm font-nunito flex-1 min-w-0">
          <span className="font-bold">Your plan is paused{untilStr ? ` until ${untilStr}` : ""}.</span>{" "}
          <span className="text-blue-100/75">Premium features resume automatically then, and your kids&rsquo; progress is safe.</span>
        </p>
        <button
          type="button"
          onClick={resumeNow}
          disabled={resuming}
          data-testid="pause-banner-resume-now"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-blue-500/25 hover:bg-blue-500/40 border border-blue-400/40 text-blue-50 px-3 py-1.5 text-xs font-bold transition disabled:opacity-50"
        >
          {resuming && <Loader2 size={12} className="animate-spin" />} Resume now
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          data-testid="pause-banner-dismiss"
          className="text-blue-200/60 hover:text-blue-100 text-lg leading-none px-1"
          aria-label="Dismiss"
        >×</button>
      </div>
    </div>
  );
}
